<script setup>
import { useBlockSafe } from '@/composables/useBlockSafe';
import Buttons from '@/components/ui/Buttons.vue';
const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
const {
  id,
  columnsCount,
  backgroundColor,
  spacingTop,
  spacingBottom,
  title,
  description,
  image,
  items,
  buttons,
  hasTitle,
  hasDescription,
  hasImage,
  hasItems,
  hasButtons,
  hasAnyContent,
} = useBlockSafe(props.data);
</script>
<template>
  <section
    v-if="hasAnyContent"
    :id="id"
    class="section"
    :class="[
      backgroundColor ? `bg-${backgroundColor}` : '',
      spacingTop ? `section--top-${spacingTop}` : 'section--top-xl',
      spacingBottom ? `section--bottom-${spacingBottom}` : 'section--bottom-xl',
      backgroundColor === 'black' ? 'white' : '',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div class="advantages">
        <div class="advantages__top max-w-784">
          <NuxtImg
            v-if="hasImage"
            :src="image.src"
            :alt="image.alt"
            :width="image.width"
            :height="image.height"
            class="advantages__image"
            data-aos="zoom-in"
            data-aos-easing="ease-out-quad"
            loading="lazy"
          />
          <div v-if="hasTitle || hasDescription" class="advantages__text">
            <h2
              v-if="hasTitle"
              class="mb-0 advantages__title h2"
              data-aos="fade-up"
              data-aos-easing="ease-out-cubic"
              v-html="title"
            ></h2>
            <div
              v-if="hasDescription"
              class="advantages__description h2 body-1-regular"
              data-aos="fade-up"
              data-aos-easing="ease-out-cubic"
              v-html="description"
            ></div>
          </div>
        </div>
        <ul
          v-if="hasItems"
          :class="[
            `m-0 p-0 advantages__items advantages__items--${columnsCount}`,
          ]"
        >
          <li
            v-for="(item, itemIndex) in items"
            :key="itemIndex"
            class="m-0 advantages__item"
            data-aos="fade-up"
            :data-aos-delay="itemIndex >= 1 ? 100 + 100 * itemIndex : 100"
          >
            <NuxtImg
              v-if="item?.icon?.src"
              :src="item?.icon?.src"
              :alt="item?.icon?.alt"
              :width="item?.icon?.width"
              :height="item?.icon?.height"
              class="advantages__item-image"
              loading="lazy"
            />
            <span
              class="advantages__item-title accent"
              :class="[backgroundColor === 'black' ? 'white' : 'black']"
              v-html="item.title"
            ></span>
          </li>
        </ul>
        <Buttons
          v-if="hasButtons"
          class="advantages__buttons"
          :background-color="backgroundColor"
          :buttons="buttons"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
          :data-delay="items && items.length > 0 ? items.length * 100 : 100"
          :buttons-prefix-class="'js-button-advantages'"
        />
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.advantages {
  display: flex;
  flex-direction: column;
  gap: 56px;
  margin: 0 auto;
  width: 100%;
  @include media-breakpoint-up(sm) {
    gap: 80px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 80);
  }
  &__title {
    margin: 0;
  }
  &__top {
    display: flex;
    flex-direction: column;
    gap: 40px;
    margin: 0 auto;
    text-align: center;
    @include media-breakpoint-up(sm) {
      gap: 64px;
    }
    @include media-breakpoint-up(md) {
      max-width: 66.2%;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 64);
    }
  }
  &__text {
    display: flex;
    flex-direction: column;
    gap: 32px;
    text-align: center;
    @include media-breakpoint-up(sm) {
      gap: 48px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 48);
    }
    &:deep(p:last-child) {
      margin: 0;
    }
  }
  &__image {
    margin: 0 auto;
    height: auto;
    width: 45%;
    @include media-breakpoint-up(sm) {
      width: 23%;
    }
    @include media-breakpoint-up(md) {
      width: 21.5%;
    }
  }
  &__description {
    @include media-breakpoint-up(sm) {
      margin: 0 auto;
      max-width: 74.6%;
    }
    @include media-breakpoint-up(md) {
      max-width: 80.3%;
    }
  }
  &__items {
    display: grid;
    gap: 32px;
    grid-template-columns: 1fr;
    list-style: none;
    @include media-breakpoint-up(sm) {
      gap: 48px;
      &--4 {
        grid-template-columns: repeat(2, 1fr);
      }
    }
    @include media-breakpoint-up(md) {
      gap: 40px;
      &--3 {
        grid-template-columns: repeat(3, 1fr);
      }
      &--4 {
        grid-template-columns: repeat(4, 1fr);
      }
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 40);
    }
  }
  &__item {
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0 16px;
    text-align: center;
    @include media-breakpoint-up(sm) {
      gap: 24px;
      padding: 0;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 24);
    }
  }
  &__buttons {
    justify-content: center;
  }
}
</style>
